/**
 * Hook for managing user settings and profile.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import * as settingsApi from '../services/settingsApi';
import type { UserProfile, UpdateProfileData } from '../types';

export function useSettings() {
  const { session, sessionEstablishing } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await settingsApi.getProfile(session.access_token);
      setProfile(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    // Wait for session to be fully established before fetching profile
    // This prevents API calls with expired tokens during background token refresh
    if (sessionEstablishing) {
      return;
    }
    fetchProfile();
  }, [session?.access_token, sessionEstablishing, fetchProfile]);

  const updateProfile = async (data: UpdateProfileData) => {
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    try {
      setError(null);
      const updated = await settingsApi.updateProfile(session.access_token, data);
      setProfile(updated);
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      setError(message);
      throw new Error(message);
    }
  };

  return {
    profile,
    loading,
    error,
    updateProfile,
    refetch: fetchProfile,
  };
}

